import { useEffect, useRef, useState, type KeyboardEvent, type ChangeEvent, type DragEvent } from "react";
import { toast } from "@/lib/toast";
import { api } from "@/api/client";
import type { ChatModelOption } from "@/api/types";
import { Paperclip, SendHorizontal, Square, X, FileText, Database, Globe, Blend, Cpu, ChevronDown, PenLine } from "lucide-react";

const MAX_MB = 50;
const MAX_FILES = 5;
// v3.10: 이미지 질의(비전 분석) — gif/bmp/webp/tiff 도 허용(서버가 JPEG 정규화)
const ALLOWED = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|hwp|png|jpe?g|gif|bmp|webp|tiff?)$/i;
// 손글씨·저품질 스캔 체크박스 노출 대상(HWOCR 판독 가능 형식): 이미지·PDF
const SCANNABLE = /\.(pdf|png|jpe?g|gif|bmp|webp|tiff?)$/i;

// ── 검색 모드 선택(명시적) ─────────────────────────────
// 사용자가 "웹검색"/"하이브리드검색"을 직접 입력하지 않고 UI에서 선택한다.
// 전송 시 rag-service 가 인식하는 prefix 를 질문 앞에 자동으로 붙인다.
//   internal(기본): prefix 없음 → 사내 지식 저장소만 검색
//   web           : "[웹검색]"   → 외부 웹 검색 (되묻기 후 진행)
//   hybrid        : "[하이브리드]" → 사내 지식 근거 + 외부 웹 검색
type SearchMode = "internal" | "web" | "hybrid";
const SEARCH_MODES: {
  mode: SearchMode;
  label: string;
  prefix: string;
  Icon: typeof Database; // 드롭박스 왼쪽에 표시할 아이콘(선택된 모드 반영)
}[] = [
  { mode: "internal", label: "내부지식", prefix: "", Icon: Database },
  { mode: "web", label: "웹검색", prefix: "[웹검색] ", Icon: Globe },
  { mode: "hybrid", label: "하이브리드", prefix: "[하이브리드] ", Icon: Blend },
];

// ── 검색 방법 선택 유지 (2026-09-30) ─────────────────
// 예전에는 전송할 때마다 '내부지식' 으로 되돌렸다(외부검색이 실수로 이어지는 것 방지).
// 사용자 요청으로 모델 선택과 같게 바꿨다 — 사용자가 고른 방법을 바꿀 때까지 유지하고,
// 새로고침·새 대화 후에도 localStorage 로 복원한다. 모르는 값이면 내부지식.
const SEARCH_MODE_KEY = "isac.chat.searchMode";
function loadSavedSearchMode(): SearchMode {
  try {
    const v = localStorage.getItem(SEARCH_MODE_KEY);
    return SEARCH_MODES.some((m) => m.mode === v) ? (v as SearchMode) : "internal";
  } catch {
    return "internal";
  }
}
function saveSearchMode(mode: SearchMode): void {
  try {
    localStorage.setItem(SEARCH_MODE_KEY, mode);
  } catch {
    /* 스토리지 차단 환경 — 저장만 생략(현재 세션 선택은 유지) */
  }
}

// ── 질의 모델 선택 유지 ─────────────────────────────
// 사용자가 고른 모델은 전송 후에도 초기화하지 않고, 재접속 시에도 복원한다.
// 저장값이 비활성/삭제된 모델이면 기본값(is_default) 모델로 폴백.
const MODEL_KEY = "isac.chat.modelId";
// Composer 밖에서 실행되는 스킬(답변 아래 '양식으로 문서 만들기', 받아쓰기 → 회의록)도 같은 선택을 쓴다.
// 비활성·삭제 모델이어도 BFF 가 chat_model 레지스트리로 걸러 서버 기본 모델이 된다.
export function loadSavedModelId(): number | null {
  try {
    const v = Number(localStorage.getItem(MODEL_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}
function saveModelId(id: number | null): void {
  try {
    if (id == null) localStorage.removeItem(MODEL_KEY);
    else localStorage.setItem(MODEL_KEY, String(id));
  } catch {
    /* 스토리지 차단 환경 — 저장만 생략(현재 세션 선택은 유지) */
  }
}

// 첨부 시 노출되는 스킬 빠른 실행 버튼(명시적 트리거)
// v3.8: 산출물 스킬 4종만 칩으로 노출. '질문'은 칩 대신 검색방법 선택 + 일반 전송으로
//       처리한다(서버가 qa 판정 → 첨부를 세션문서로 올려 참조 답변). '비교'는 텍스트
//       지시("두 문서 비교해줘")로 여전히 실행 가능.
// minFiles: 해당 스킬 실행에 필요한 최소 첨부 수
// maxFiles / only: 양식 문서 작성(doc_fill)은 docx·pptx·xlsx 양식 정확히 1개일 때만 활성.
//   질문을 입력하면 지식저장소 답변으로, 비워 두면 직전 답변으로 양식을 채운다.
const DOC_FILL_EXT_RE = /\.(docx|pptx|xlsx)$/i;
const SKILL_CHIPS: { hint: string; label: string; minFiles: number; maxFiles?: number; only?: RegExp; title?: string }[] = [
  { hint: "meeting", label: "회의록 작성", minFiles: 1 },
  { hint: "summary", label: "요약서 작성", minFiles: 1 },
  { hint: "translate", label: "번역서 작성", minFiles: 1 },
  { hint: "analyze", label: "분석서 작성", minFiles: 1 },
  { hint: "doc_fill", label: "양식으로 작성", minFiles: 1, maxFiles: 1, only: DOC_FILL_EXT_RE,
    title: "첨부한 양식(docx·pptx·xlsx 1개)의 빈칸을 답변 내용으로 채워 같은 형식의 문서를 만듭니다" },
];

function chipEnabled(c: (typeof SKILL_CHIPS)[number], files: File[]): boolean {
  if (files.length < c.minFiles) return false;
  if (c.maxFiles && files.length > c.maxFiles) return false;
  if (c.only && !files.every((f) => c.only!.test(f.name))) return false;
  return true;
}

export default function Composer({
  busy,
  onSend,
  onStop,
  onSkillSend,
  seed,
}: {
  busy: boolean;
  onSend: (text: string, modelId?: number | null) => void;
  onStop: () => void;
  onSkillSend: (files: File[], text: string, hint: string, modelId?: number | null, hwocr?: boolean) => void;
  seed?: { text: string; nonce: number };
}) {
  const [text, setText] = useState("");
  const [searchMode, setSearchModeState] = useState<SearchMode>(loadSavedSearchMode);
  // 사용자가 고른 검색 방법은 저장해 두고 전송 후에도 유지한다(자동 복귀 없음).
  const setSearchMode = (mode: SearchMode) => {
    setSearchModeState(mode);
    saveSearchMode(mode);
  };
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  // ── 손글씨·저품질 스캔(HWOCR) 지정 ──
  // 이미지/PDF 첨부 시 체크박스 노출. 체크하면 서버가 기본 OCR 대신
  // HWOCR(LLM 비전)로 강제 판독한다. 전송 후 해제(고비용 판독 지속 방지).
  const [hwocr, setHwocr] = useState(false);
  // ── 질의 모델 선택(관리자 등록 목록) ──
  // 최초 진입은 기본값(is_default) 모델. 사용자가 다른 모델을 고르면 그 선택을
  // 그대로 유지한다 — 전송 후 복귀 없음 + localStorage 복원(재접속 후에도 유지).
  const [models, setModels] = useState<ChatModelOption[]>([]);
  const [modelId, setModelId] = useState<number | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // 모델 목록 로드(1회). 실패/빈 목록이면 선택 UI 미노출 → 기존 동작 유지.
  useEffect(() => {
    let alive = true;
    api
      .models()
      .then((r) => {
        if (!alive) return;
        const items = r.items ?? [];
        setModels(items);
        // 저장된 선택이 아직 활성 목록에 있으면 복원, 아니면 기본값으로 폴백
        const saved = loadSavedModelId();
        const fallback = items.find((m) => m.is_default)?.id ?? items[0]?.id ?? null;
        setModelId(saved !== null && items.some((m) => m.id === saved) ? saved : fallback);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // 메시지 편집 → 입력창에 채우기(부모가 seed.nonce 변경)
  useEffect(() => {
    if (seed) {
      setText(seed.text);
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (ta) {
          ta.focus();
          ta.style.height = "auto";
          ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed?.nonce]);

  const resetHeight = () => {
    if (taRef.current) taRef.current.style.height = "auto";
  };
  const autoGrow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  };

  // 모델 선택 변경 — 즉시 저장해 다음 질문·재접속에도 그대로 적용
  const selectModel = (id: number | null) => {
    setModelId(id);
    saveModelId(id);
  };

  const clearAll = () => {
    setPendingFiles([]);
    setText("");
    setHwocr(false); // 전송·초기화 시 손글씨 지정 해제
    resetHeight();
  };

  const submit = () => {
    if (busy) return;
    const v = text.trim();
    if (pendingFiles.length > 0) {
      // 첨부가 있으면 스킬 경로로(서버가 액션 라우팅 → 회의록/요약/비교 등, qa면 일반 질의 폴백).
      // 검색모드 prefix 도 함께 전달 — 웹검색/하이브리드 선택 시 서버가
      // mode_prefix 가드로 qa 직행(첨부는 세션문서 컨텍스트)한다.
      const prefix = SEARCH_MODES.find((m) => m.mode === searchMode)?.prefix ?? "";
      if (prefix && !v) {
        toast("웹검색/하이브리드 질의는 질문 내용을 입력해 주세요.", "error");
        return;
      }
      onSkillSend(pendingFiles, prefix + v, "", modelId, hwocr);
      clearAll();   // 검색 방법은 유지한다 (2026-09-30)
      return;
    }
    if (!v) return;
    // 선택한 검색 모드에 맞는 prefix 를 붙여 전송(rag-service 가 해석).
    const prefix = SEARCH_MODES.find((m) => m.mode === searchMode)?.prefix ?? "";
    onSend(prefix + v, modelId);
    setText("");   // 검색 방법은 사용자가 바꿀 때까지 유지한다 (2026-09-30)
    resetHeight();
  };

  const runChip = (hint: string, minFiles: number) => {
    if (busy || pendingFiles.length === 0) return;
    if (pendingFiles.length < minFiles) {
      toast(`파일이 ${minFiles}개 이상 필요합니다.`, "error");
      return;
    }
    const chip = SKILL_CHIPS.find((c) => c.hint === hint);
    if (chip && !chipEnabled(chip, pendingFiles)) {
      toast(hint === "doc_fill" ? "양식은 docx·pptx·xlsx 파일 1개만 첨부해 주세요." : "첨부 조건이 맞지 않습니다.", "error");
      return;
    }
    onSkillSend(pendingFiles, text.trim(), hint, modelId, hwocr);
    clearAll();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // 파일 추가(검증 + 중복/개수 제한). 기존 목록에 누적.
  const addFiles = (incoming: File[]) => {
    setPendingFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (!ALLOWED.test(f.name)) {
          toast(`지원하지 않는 형식: ${f.name}`, "error");
          continue;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
          toast(`${f.name}: ${MAX_MB}MB 이하만 가능합니다.`, "error");
          continue;
        }
        if (next.some((p) => p.name === f.name && p.size === f.size)) continue; // 중복 제거
        if (next.length >= MAX_FILES) {
          toast(`최대 ${MAX_FILES}개까지 첨부할 수 있습니다.`, "error");
          break;
        }
        next.push(f);
      }
      return next;
    });
  };

  const onPickFile = (e: ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (list.length) addFiles(list);
  };

  const removeAt = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  // ── 드래그앤드롭 ──
  const onDragOver = (e: DragEvent) => {
    if (busy) return;
    if (Array.from(e.dataTransfer.types || []).includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const list = Array.from(e.dataTransfer.files ?? []);
    if (list.length) addFiles(list);
  };

  const has = pendingFiles.length > 0;
  // 이미지/PDF 첨부가 있을 때만 손글씨 체크박스 노출.
  // 대상 파일이 모두 제거되면 체크 상태도 해제한다.
  const scannable = pendingFiles.some((f) => SCANNABLE.test(f.name));
  useEffect(() => {
    if (!scannable && hwocr) setHwocr(false);
  }, [scannable, hwocr]);

  return (
    <div
      className="relative border-t border-border bg-background/90 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 m-2 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 text-sm font-medium text-primary">
          여기에 파일을 놓으세요 (최대 {MAX_FILES}개)
        </div>
      )}
      <div className="mx-auto w-full max-w-3xl px-3 py-2.5">
        {has && (
          <div className="mb-2 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {pendingFiles.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs"
                >
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="max-w-[45vw] truncate">{f.name}</span>
                  <button onClick={() => removeAt(i)} aria-label="첨부 제거">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {pendingFiles.length > 1 && (
                <button onClick={() => setPendingFiles([])} className="text-xs text-muted-foreground underline">
                  전체 제거
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SKILL_CHIPS.map((c) => {
                const enabled = !busy && chipEnabled(c, pendingFiles);
                return (
                  <button
                    key={c.hint}
                    onClick={() => runChip(c.hint, c.minFiles)}
                    disabled={!enabled}
                    title={
                      pendingFiles.length < c.minFiles
                        ? `파일 ${c.minFiles}개 이상 필요`
                        : !chipEnabled(c, pendingFiles) && c.hint === "doc_fill"
                          ? "양식은 docx·pptx·xlsx 파일 1개만"
                          : c.title
                    }
                    className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:bg-muted disabled:opacity-40"
                  >
                    {c.label}
                  </button>
                );
              })}
              <span className="self-center text-xs text-muted-foreground">
                또는 질문·지시를 입력하고 전송하면 첨부를 참고해 답변합니다 (스크린샷·사진·도면은 이미지를 직접 분석)
              </span>
            </div>
            {/* 손글씨·저품질 스캔 지정(HWOCR) — 이미지/PDF 첨부 시에만 노출.
                체크하면 기본 문자인식(인쇄체 전용) 대신 AI 비전으로 정밀 판독한다. */}
            {scannable && (
              <label className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:bg-muted">
                <input
                  type="checkbox"
                  checked={hwocr}
                  onChange={(e) => setHwocr(e.target.checked)}
                  disabled={busy}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <PenLine className="h-3.5 w-3.5 text-muted-foreground" />
                손글씨·저품질 스캔 (AI 정밀 판독)
                <span className="text-muted-foreground">— 필기 메모·점검일지 등 기본 인식이 어려운 문서</span>
              </label>
            )}
          </div>
        )}
        {/* 검색 방법 + 질의 모델 — 아이콘이 붙은 드롭박스 2개를 한 줄로 표시.
            (native select 는 option 안에 아이콘을 못 넣으므로, 아이콘을 select 왼쪽에
             오버레이하고 검색 방법은 선택된 모드의 아이콘으로 바뀐다)
            검색 방법: 전송 시 prefix 자동 주입, 사용자가 바꿀 때까지 유지(localStorage 복원).
            모델: 사용자 선택을 유지(localStorage 복원), 저장값이 비활성이면 기본값 모델. */}
        <div className="mb-2 flex items-center gap-2">
          {(() => {
            const ModeIcon = SEARCH_MODES.find((m) => m.mode === searchMode)?.Icon ?? Database;
            return (
              <div className="relative">
                <ModeIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <select
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value as SearchMode)}
                  disabled={busy}
                  aria-label="검색 방법"
                  title="검색 방법"
                  className="h-8 appearance-none rounded-lg border border-border bg-card pl-8 pr-7 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-40"
                >
                  {SEARCH_MODES.map(({ mode, label }) => (
                    <option key={mode} value={mode}>
                      {label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              </div>
            );
          })()}
          {models.length > 0 && (
            <div className="relative">
              <Cpu className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <select
                value={modelId ?? ""}
                onChange={(e) => selectModel(e.target.value ? Number(e.target.value) : null)}
                disabled={busy}
                aria-label="모델"
                title="모델 (선택 유지 — 다음 질문에도 계속 적용)"
                className="h-8 appearance-none rounded-lg border border-border bg-card pl-8 pr-7 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-40"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.is_default ? " (기본)" : ""}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground active:bg-muted"
            aria-label="파일 첨부"
            title="파일 첨부(여러 개 가능 · 드래그앤드롭 지원)"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.png,.jpg,.jpeg,.gif,.bmp,.webp,.tif,.tiff"
            onChange={onPickFile}
          />
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              autoGrow();
            }}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={has ? "지시 입력(예: 두 문서 비교해줘)…" : "무엇이든 물어보세요…"}
            className="max-h-40 flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {busy ? (
            <button
              onClick={onStop}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EB4E26] text-white"
              aria-label="생성 중지"
              title="생성 중지"
            >
              <Square className="h-4 w-4 fill-current" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!text.trim() && !has}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-40"
              aria-label="전송"
            >
              <SendHorizontal className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
